import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api } from "./api";
import type { User } from "./types";

type Mode = "login" | "register" | "2fa" | "2fa_setup" | "pending" | "registered";

export function Auth({ onAuthed, resumeSetup }: { onAuthed: (u: User) => void; resumeSetup?: boolean }) {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // mandatory 2FA enrollment state
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");
  const [recovery, setRecovery] = useState<string[]>([]);

  // Resume mandatory 2FA enrollment after a reload (session is in setup_2fa state).
  useEffect(() => {
    if (resumeSetup) beginSetup().catch(() => setMode("login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeSetup]);

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.login({ email, password });
      if (r.status === "2fa_required") { setMode("2fa"); }
      else if (r.status === "2fa_setup_required") { await beginSetup(); }
    } catch (e: any) {
      if (e.body?.error === "not_approved")
        setErr(e.body.status === "rejected"
          ? "Dein Zugang wurde abgelehnt."
          : "Dein Konto wartet noch auf Freigabe durch den Admin.");
      else if (e.message === "invalid_credentials") setErr("E-Mail oder Passwort falsch.");
      else setErr("Anmeldung fehlgeschlagen.");
    } finally { setBusy(false); }
  }

  async function submitRegister(e: React.FormEvent) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.register({ email, password, name });
      setMode(r.approval === "approved" ? "registered" : "pending");
    } catch (e: any) {
      const map: Record<string, string> = {
        email_taken: "Diese E-Mail ist bereits registriert.",
        weak_password: "Passwort: mindestens 8 Zeichen.",
        invalid_email: "Bitte eine gültige E-Mail eingeben.",
        invalid_domain: e.body?.message || "Nur @tactyc.studio-Adressen sind erlaubt.",
      };
      setErr(map[e.message] || "Registrierung fehlgeschlagen.");
    } finally { setBusy(false); }
  }

  async function submit2fa(e: React.FormEvent) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.challenge2fa(code);
      onAuthed(r.user);
    } catch { setErr("Code ungültig. Bitte erneut versuchen."); }
    finally { setBusy(false); }
  }

  async function beginSetup() {
    const r = await api.setup2fa();
    setSecret(r.secret);
    setQr(await QRCode.toDataURL(r.uri, { margin: 1, width: 200 }));
    setMode("2fa_setup");
  }

  async function confirmSetup(e: React.FormEvent) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.enable2fa(code);
      if (r.recoveryCodes?.length) { setRecovery(r.recoveryCodes); return; }
    } catch { setErr("Code ungültig."); }
    finally { setBusy(false); }
  }

  async function finishSetup() {
    const me = await api.me();
    onAuthed(me.user);
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="brand">
          <div className="brand-dot">T</div>
          <div className="brand-name">TACTYC</div>
        </div>

        {mode === "login" && (
          <form onSubmit={submitLogin}>
            <h1>Willkommen zurück</h1>
            <p className="sub">Melde dich mit deiner @tactyc.studio-Adresse an.</p>
            <div className="field"><label>E-Mail</label>
              <input className="input" type="email" value={email} autoComplete="email"
                onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="field"><label>Passwort</label>
              <input className="input" type="password" value={password} autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} required /></div>
            <div className="err">{err}</div>
            <button className="btn" disabled={busy}>{busy ? "…" : "Anmelden"}</button>
            <div className="link-row">Noch kein Konto?{" "}
              <a onClick={() => { setMode("register"); setErr(""); }} href="#">Registrieren</a></div>
          </form>
        )}

        {mode === "register" && (
          <form onSubmit={submitRegister}>
            <h1>Konto anfragen</h1>
            <p className="sub">Nur <strong>@tactyc.studio</strong>-Adressen. Nach der Registrierung gibt ein Admin deinen Zugang frei.</p>
            <div className="field"><label>Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="field"><label>E-Mail</label>
              <input className="input" type="email" value={email} autoComplete="email" placeholder="vorname@tactyc.studio"
                onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="field"><label>Passwort</label>
              <input className="input" type="password" value={password} autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)} required minLength={8} /></div>
            <div className="err">{err}</div>
            <button className="btn" disabled={busy}>{busy ? "…" : "Registrieren"}</button>
            <div className="link-row">Bereits ein Konto?{" "}
              <a onClick={() => { setMode("login"); setErr(""); }} href="#">Anmelden</a></div>
          </form>
        )}

        {mode === "pending" && (
          <div>
            <h1>Fast geschafft</h1>
            <p className="sub">Dein Konto wurde angelegt und wartet auf <strong>Freigabe durch einen Admin</strong>.
              Du kannst dich anmelden, sobald dein Zugang freigegeben wurde.</p>
            <button className="btn" onClick={() => { setMode("login"); setErr(""); }}>Zur Anmeldung</button>
          </div>
        )}

        {mode === "registered" && (
          <div>
            <h1>Konto erstellt</h1>
            <p className="sub">Dein Admin-Konto ist freigegeben. Melde dich jetzt an — beim ersten Login richtest du die 2-Faktor-Authentifizierung ein.</p>
            <button className="btn" onClick={() => { setMode("login"); setErr(""); }}>Anmelden</button>
          </div>
        )}

        {mode === "2fa" && (
          <form onSubmit={submit2fa}>
            <h1>Zwei-Faktor</h1>
            <p className="sub">Gib den 6-stelligen Code aus deiner Authenticator-App ein (oder einen Recovery-Code).</p>
            <div className="field">
              <input className="input code-input" value={code} inputMode="numeric" autoFocus
                onChange={(e) => setCode(e.target.value)} placeholder="000000" /></div>
            <div className="err">{err}</div>
            <button className="btn" disabled={busy}>{busy ? "…" : "Bestätigen"}</button>
            <div className="link-row"><a onClick={() => { setMode("login"); setErr(""); setCode(""); }} href="#">Zurück</a></div>
          </form>
        )}

        {mode === "2fa_setup" && (
          recovery.length ? (
            <div>
              <h1>Recovery-Codes</h1>
              <p className="sub">2FA ist aktiv! Bewahre diese Codes sicher auf — jeder funktioniert einmal, falls du keinen Zugriff auf deine App hast.</p>
              <div className="recovery-grid">{recovery.map((c) => <div key={c}>{c}</div>)}</div>
              <button className="btn" onClick={finishSetup}>Weiter zum Board</button>
            </div>
          ) : (
            <form onSubmit={confirmSetup}>
              <h1>2FA einrichten (Pflicht)</h1>
              <p className="sub">Scanne den QR-Code mit Google Authenticator, Authy oder 1Password.</p>
              <div className="qr">{qr && <img src={qr} width={180} height={180} alt="QR" />}</div>
              <p className="muted">Oder manuell:</p>
              <div className="secret-box">{secret}</div>
              <div className="field" style={{ marginTop: 14 }}><label>6-stelliger Code</label>
                <input className="input code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" /></div>
              <div className="err">{err}</div>
              <button className="btn" disabled={busy}>{busy ? "…" : "Aktivieren"}</button>
            </form>
          )
        )}
      </div>
    </div>
  );
}
